import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface ElevenlabsTtsGeneratorParams {
  /**
   * 任务唯一标识符
   */
  task_id: string;
  data: {
    /**
     * 要转换为语音的文本内容
     */
    text: string;
    /**
     * 声音ID，默认为Rachel (21m00Tcm4TlvDq8ikWAM)。可选其他声音如：Josh, Bella, Antoni等
     */
    voice_id?: string;
    /**
     * 模型ID，可选值：eleven_monolingual_v1, eleven_multilingual_v1, eleven_multilingual_v2, eleven_turbo_v2等
     */
    model_id?: string;
    /**
     * 输出音频格式，默认mp3_44100_128。可选：mp3_22050_32, mp3_44100_64, mp3_44100_96, mp3_44100_128, mp3_44100_192, pcm_16000, pcm_22050, pcm_24000, pcm_44100, ulaw_8000
     */
    output_format?: string;
    /**
     * 语言代码，如：en, zh, es, fr等
     */
    language_code?: string;
    /**
     * 语音稳定性，范围0-1。值越高语音越稳定，越低则越富有表现力
     * @range 0 - 1
     */
    stability?: number;
    /**
     * 相似度增强，范围0-1。控制声音与原始声音的相似程度
     * @range 0 - 1
     */
    similarity_boost?: number;
    /**
     * 风格强度，范围0-1。控制语音的表现风格
     * @range 0 - 1
     */
    style?: number;
    /**
     * 是否启用说话者增强功能
     */
    use_speaker_boost?: boolean;
    /**
     * 随机种子，用于可重现的生成结果
     */
    seed?: number;
    /**
     * 前置文本，用于提供上下文以获得更自然的语音
     */
    previous_text?: string;
    /**
     * 后续文本，用于提供上下文以获得更自然的语音
     */
    next_text?: string;
    /**
     * 优化流式延迟级别，范围0-4。值越高延迟越低，但可能影响质量
     */
    optimize_streaming_latency?: number;
    /**
     * 发音字典定位器数组，用于自定义发音
     */
    pronunciation_dictionary_locators?: Array<{
    }>;
  };
}

export interface ElevenlabsTtsGeneratorResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * elevenlabs_tts_generator
 *
 * <p>使用ElevenLabs AI将文本转换为自然流畅的语音</p>
 * 
 *
 * @example
 * ```typescript
 * import { initSeacloud, elevenlabsTtsGenerator } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await elevenlabsTtsGenerator({
 *   // 参数见 ElevenlabsTtsGeneratorParams 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 ElevenlabsTtsGeneratorParams 接口定义
 * @returns 生成的资源列表
 */
export async function elevenlabsTtsGenerator(
  params: ElevenlabsTtsGeneratorParams,
): Promise<ElevenlabsTtsGeneratorResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 2000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'elevenlabs_tts_generator',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: ElevenlabsTtsGeneratorResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
